import { useEffect, useRef, useState } from "react";
import { Volume2, RefreshCcw, Play, Rabbit, Turtle } from "lucide-react";
import { speak } from "@/lib/tts";
import { IDS } from "@/lib/testIds";
import { BLENDING_WORDS, randomFrom } from "@/lib/gameData";
import { BLEND_SLOW_MS, BLEND_FAST_MS } from "@/lib/constants";

function nextWord(current) {
  let pick = randomFrom(BLENDING_WORDS);
  // Avoid picking the same word twice in a row when possible
  let guard = 5;
  while (current && pick.word === current.word && guard-- > 0) {
    pick = randomFrom(BLENDING_WORDS);
  }
  return pick;
}

export default function BlendingStudio() {
  const [item, setItem] = useState(() => nextWord(null));
  const [activeIndex, setActiveIndex] = useState(-1); // which phoneme is currently glowing
  const [playing, setPlaying] = useState(false);
  const [revealed, setRevealed] = useState(false);
  const timersRef = useRef([]);

  const clearTimers = () => {
    timersRef.current.forEach((id) => clearTimeout(id));
    timersRef.current = [];
  };

  useEffect(() => {
    return () => clearTimers();
  }, []);

  useEffect(() => {
    // Reset UI whenever the word changes
    clearTimers();
    setActiveIndex(-1);
    setPlaying(false);
    setRevealed(false);
  }, [item]);

  const playPhonemes = (gapMs) => {
    clearTimers();
    setPlaying(true);
    setRevealed(false);
    item.phonemes.forEach((ph, i) => {
      const t = setTimeout(() => {
        setActiveIndex(i);
        speak(ph, { rate: 0.75 });
      }, i * gapMs);
      timersRef.current.push(t);
    });
    // After the last phoneme, clear highlight
    const clearAt = item.phonemes.length * gapMs + 400;
    const clearT = setTimeout(() => {
      setActiveIndex(-1);
      setPlaying(false);
    }, clearAt);
    timersRef.current.push(clearT);
  };

  const playBlended = () => {
    clearTimers();
    setPlaying(true);
    setRevealed(true);
    speak(item.word, { rate: 0.9 });
    const t = setTimeout(() => setPlaying(false), 900);
    timersRef.current.push(t);
  };

  const handleNext = () => {
    setItem((cur) => nextWord(cur));
  };

  const handlePhonemeClick = (i, ph) => {
    setActiveIndex(i);
    speak(ph, { rate: 0.75 });
    // Auto-clear highlight
    const t = setTimeout(() => setActiveIndex(-1), 700);
    timersRef.current.push(t);
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div className="inline-flex items-center gap-2 bg-emerald-50 text-emerald-700 px-4 py-2 rounded-full font-bold text-sm">
          Blending Studio
        </div>
        <button
          data-testid={IDS.games.blend.next}
          onClick={handleNext}
          className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 bg-slate-100 hover:bg-slate-200 font-bold text-sm text-slate-700 transition-colors"
        >
          <RefreshCcw className="w-4 h-4" /> New word
        </button>
      </div>

      <div className="text-center bg-slate-50 rounded-3xl p-8 mb-6">
        <div className="text-7xl mb-3">{item.emoji}</div>
        <p className="text-sm font-bold uppercase tracking-widest text-slate-500">
          Blend the sounds &mdash; then say the word
        </p>
      </div>

      {/* Phoneme tiles */}
      <div className="flex flex-wrap justify-center gap-3 sm:gap-4 mb-6">
        {item.phonemes.map((ph, i) => {
          const isActive = activeIndex === i;
          return (
            <button
              key={`ph-${i}-${ph}`}
              data-testid={IDS.games.blend.phoneme(i)}
              onClick={() => handlePhonemeClick(i, ph)}
              className={`min-w-[70px] sm:min-w-[90px] h-20 sm:h-24 px-4 rounded-2xl border-2 font-display text-3xl sm:text-4xl font-semibold transition-all
                ${isActive
                  ? "bg-emerald-500 border-emerald-600 text-white scale-110 shadow-lg phonx-letter-pop"
                  : "bg-white border-slate-200 text-slate-800 hover:border-emerald-400 hover:text-emerald-600 hover:-translate-y-0.5 shadow-sm hover:shadow-md"
                }
              `}
            >
              /{ph}/
            </button>
          );
        })}
      </div>

      {/* Blended word reveal */}
      <div
        className={`text-center mb-6 transition-all duration-300 ${
          revealed ? "opacity-100 scale-100" : "opacity-40 scale-95"
        }`}
      >
        <p className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-1">
          Whole word
        </p>
        <div
          data-testid={IDS.games.blend.word}
          className="font-display text-5xl sm:text-6xl font-semibold text-emerald-600 leading-none"
        >
          {revealed ? item.word : "•".repeat(item.word.length)}
        </div>
      </div>

      {/* Controls */}
      <div className="flex flex-wrap justify-center gap-3">
        <button
          data-testid={IDS.games.blend.playPhonemes}
          onClick={() => playPhonemes(BLEND_SLOW_MS)}
          disabled={playing}
          className="inline-flex items-center gap-2 rounded-full px-5 py-3 bg-white border-2 border-slate-200 text-slate-800 font-bold hover:border-emerald-400 hover:text-emerald-600 disabled:opacity-50 transition-colors"
        >
          <Turtle className="w-5 h-5" /> Slow sounds
        </button>
        <button
          data-testid="blend-play-fast"
          onClick={() => playPhonemes(BLEND_FAST_MS)}
          disabled={playing}
          className="inline-flex items-center gap-2 rounded-full px-5 py-3 bg-white border-2 border-slate-200 text-slate-800 font-bold hover:border-emerald-400 hover:text-emerald-600 disabled:opacity-50 transition-colors"
        >
          <Rabbit className="w-5 h-5" /> Fast sounds
        </button>
        <button
          data-testid={IDS.games.blend.playBlended}
          onClick={playBlended}
          disabled={playing}
          className="inline-flex items-center gap-2 rounded-full px-6 py-3 bg-emerald-500 hover:bg-emerald-600 text-white font-bold shadow-md hover:shadow-lg transition-all disabled:opacity-50"
        >
          <Play className="w-5 h-5" fill="currentColor" /> Blend it!
        </button>
        <button
          data-testid={IDS.games.blend.reveal}
          onClick={() => {
            setRevealed(true);
            speak(item.word, { rate: 0.9 });
          }}
          className="inline-flex items-center gap-2 rounded-full px-5 py-3 bg-emerald-50 text-emerald-700 font-bold hover:bg-emerald-100 transition-colors"
        >
          <Volume2 className="w-5 h-5" /> Hear whole word
        </button>
      </div>
    </div>
  );
}
